import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import api from '@/lib/api'
import { LoadingSpinner } from '@/components/ui'
import {
  FileUp,
  Play,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react'

interface ProviderStatus {
  id: string
  name: string
  type: string
  description: string
  status: string
  last_sync_date: string | null
  data_coverage: { min_date: string; max_date: string; row_count: number } | null
  can_trigger: boolean
}

const statusConfig: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  not_configured: { label: 'Not Configured', color: 'bg-gray-100 text-gray-500 border-gray-200', icon: WifiOff },
}

const typeConfig: Record<string, { label: string; color: string }> = {
  api: { label: 'API', color: 'bg-blue-100 text-blue-700' },
  file: { label: 'File', color: 'bg-purple-100 text-purple-700' },
}

export default function DataProviders() {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)

  const { data: providers, isLoading } = useQuery<ProviderStatus[]>({
    queryKey: ['data-providers-status'],
    queryFn: () => api.get('/data-providers/status').then((r) => r.data),
  })

  const syncMutation = useMutation({
    mutationFn: async ({ providerId, file }: { providerId: string; file?: File }) => {
      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/data-providers/sync/${providerId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then((r) => r.data)
      }
      return api.post(`/data-providers/sync/${providerId}`).then((r) => r.data)
    },
    onSuccess: (data, { providerId }) => {
      const results = data.results
      let text = `${providerId.toUpperCase()} sync completed.`
      if (results?.peer_count !== undefined) {
        const dbInfo = results.db_import?.imported
          ? ` (${results.db_import.imported} imported to DB)`
          : ''
        text = results.status === 'already_synced'
          ? `Legacy: ${results.peer_count.toLocaleString()} peers cached${dbInfo}.`
          : `Legacy: synced ${results.peer_count?.toLocaleString() || '?'} peer files${dbInfo}.`
      } else if (results?.sp500tr || results?.vix) {
        const parts: string[] = []
        if (results.sp500tr) parts.push(`S&P 500 TR: ${results.sp500tr.inserted} new`)
        if (results.vix) parts.push(`VIX: ${results.vix.inserted} new`)
        text = parts.join(', ')
      } else if (results?.inserted !== undefined) {
        text = `${providerId.toUpperCase()}: ${results.inserted} inserted, ${results.skipped ?? 0} skipped.`
      }
      setMessage({ type: 'success', text })
      setSyncingId(null)
      queryClient.invalidateQueries({ queryKey: ['data-providers-status'] })
      setTimeout(() => setMessage(null), 8000)
    },
    onError: (err: any, { providerId }) => {
      const detail = err.response?.data?.detail || 'Sync failed'
      setMessage({ type: 'error', text: `${providerId.toUpperCase()}: ${detail}` })
      setSyncingId(null)
      setTimeout(() => setMessage(null), 8000)
    },
  })

  const handleSync = (providerId: string) => {
    setSyncingId(providerId)
    syncMutation.mutate({ providerId })
  }

  const handleFileSync = (providerId: string, file: File) => {
    setSyncingId(providerId)
    syncMutation.mutate({ providerId, file })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner message="Loading data providers..." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Providers</h1>
          <p className="text-muted-foreground">
            Monitor data sources and trigger manual syncs
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['data-providers-status'] })}
          className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {message && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 shrink-0" />
          )}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {providers?.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            isSyncing={syncingId === provider.id}
            onSync={handleSync}
            onFileSync={handleFileSync}
          />
        ))}
      </div>

      {(!providers || providers.length === 0) && (
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
          No data providers configured.
        </div>
      )}
    </div>
  )
}

function ProviderCard({
  provider,
  isSyncing,
  onSync,
  onFileSync,
}: {
  provider: ProviderStatus
  isSyncing: boolean
  onSync: (id: string) => void
  onFileSync: (id: string, file: File) => void
}) {
  const status = statusConfig[provider.status] || statusConfig.not_configured
  const type = typeConfig[provider.type] || typeConfig.api
  const StatusIcon = status.icon

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) onFileSync(provider.id, acceptedFiles[0])
  }, [provider.id, onFileSync])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    multiple: false,
    noClick: false,
  })

  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {provider.type === 'api' ? (
            <Wifi className="h-5 w-5 text-blue-500" />
          ) : (
            <FileUp className="h-5 w-5 text-purple-500" />
          )}
          <h3 className="font-semibold text-sm">{provider.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${type.color}`}>
            {type.label}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium border ${status.color}`}
          >
            <StatusIcon className="h-3 w-3 inline mr-1" />
            {status.label}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground mb-4">{provider.description}</p>

      {/* Coverage */}
      {provider.data_coverage && (
        <div className="bg-muted/50 rounded-md p-3 mb-4 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date range:</span>
            <span className="font-medium">
              {provider.data_coverage.min_date} &mdash; {provider.data_coverage.max_date}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Rows:</span>
            <span className="font-medium">{provider.data_coverage.row_count.toLocaleString()}</span>
          </div>
          {provider.last_sync_date && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last sync:</span>
              <span className="font-medium">{provider.last_sync_date}</span>
            </div>
          )}
        </div>
      )}

      {!provider.data_coverage && provider.status !== 'not_configured' && (
        <div className="bg-muted/50 rounded-md p-3 mb-4 text-xs text-muted-foreground text-center">
          No data available
        </div>
      )}

      {/* Spacer to push button to bottom */}
      <div className="flex-1" />

      {/* Action */}
      {provider.can_trigger && provider.type === 'file' ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-md p-3 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
        >
          <input {...getInputProps()} />
          {isSyncing ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Importing...
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-2">
                Drop a CSV or Excel file, or click to browse
              </p>
              <span className="inline-flex items-center gap-2 h-8 px-3 bg-primary text-primary-foreground rounded-md text-xs font-medium">
                <FileUp className="h-3.5 w-3.5" />
                Choose File
              </span>
            </>
          )}
        </div>
      ) : provider.can_trigger ? (
        <button
          onClick={() => onSync(provider.id)}
          disabled={isSyncing}
          className="w-full h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSyncing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Sync Now
            </>
          )}
        </button>
      ) : (
        <button
          disabled
          className="w-full h-9 px-4 bg-muted text-muted-foreground rounded-md text-sm font-medium cursor-not-allowed"
        >
          {provider.status === 'active'
            ? 'Auto-refreshed'
            : provider.status === 'inactive'
            ? 'No manual sync'
            : 'Coming Soon'}
        </button>
      )}
    </div>
  )
}
